import React, { useState, useEffect } from 'react';
import { jwtDecode } from 'jwt-decode';
import { useHistory } from 'react-router-dom';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ArrowForward from '@mui/icons-material/ArrowForward';
import ActivateIcon from '@mui/icons-material/CheckCircle';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { fetchData, deleteData, activateData } from '../../../apiService';
import Link from 'react-router-dom/Link';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import SearchIcon from '@mui/icons-material/Search';
import Button from '@mui/material/Button';
import CopyrightIcon from '@mui/icons-material/Copyright';
import AddIcon from '@mui/icons-material/Add';
import { Tooltip } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';

function WorkDetailsIndex() {
    const [data, setData] = useState([]);
    const [deleteItemId, setDeleteItemId] = useState(null);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const history = useHistory(); // Access the navigation history
    const [loading, setLoading] = useState(false);
    const [isFocused, setIsFocused] = useState(false);

    const { isAuthenticated, companyId, branchId, permissions, token, logout } = useAuth();
    const hasCreateMasterPermission = permissions.includes('Create Master');
    const hasEditMasterPermission = permissions.includes('Edit Master');
    const hasDeleteMasterPermission = permissions.includes('Delete Master');

    useEffect(() => {
        // Check if the token is expired
        if (isAuthenticated && tokenIsExpired(token)) {
            // Token is expired, log out the user
            logout();
            return;
        }
        // Token is not expired, fetch data
        fetchtableData();
    }, [isAuthenticated, companyId, branchId]);

    const [searchQuery, setSearchQuery] = useState('');
    const [showSearchInput, setShowSearchInput] = useState(false);

    //search
    const handleSearch = (event) => {
        setSearchQuery(event.target.value);
    };

    const toggleSearchInput = () => {
        setShowSearchInput(!showSearchInput);
    };

    const filteredData = data.filter((item) =>
        Object.values(item).some((value) =>
            String(value).toLowerCase().includes(searchQuery.toLowerCase())
        )
    );

    const fetchtableData = async () => {
        setLoading(true);
        fetchData('WorkDetailsApi', `/${companyId}`, token)
            .then((response) => {
                if (response.responseCode == '1111') {
                    console.log(response);
                    setData(response.response_data);
                }
            })
            .catch((error) => {
                console.error('Error fetching Work Details:', error);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const handleDelete = async () => {
        deleteData('WorkDetailsApi', `${deleteItemId}`, token)
            .then((response) => {
                fetchtableData();
                setDeleteItemId(null);
                setConfirmDialogOpen(false);
            })
            .catch((error) => {
                console.error('Error deleting record:', error);
            });
    };

    const handleActivate = (itemId) => {
        activateData('WorkDetailsApi', `${itemId}`)
            .then(() => {
                fetchtableData();
            })
            .catch((error) => {
                console.error('Error activating record:', error);
            });
    };

    const openConfirmDialog = (itemId) => {
        setDeleteItemId(itemId);
        setConfirmDialogOpen(true);
    };

    const closeConfirmDialog = () => {
        setDeleteItemId(null);
        setConfirmDialogOpen(false);
    };

    const groupedData = filteredData.reduce((acc, item) => {
        const key = item.WorkName;
        if (!acc[key]) {
            acc[key] = [];
        }
        acc[key].push(item);
        return acc;
    }, {});

    return (
        <div className="container mt-2 mx-auto" style={{ maxWidth: '97%' }}>
            <div className='mb-2 flex justify-between'>
                <h6 className='block uppercase tracking-wide text-black productHead flex-grow linkcolor' style={{ fontFamily: 'Georgia, Cambria, "Times New Roman", Times, serif' }}>Item Details</h6>
                {loading && (
                    <div style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 9999
                    }}>
                        <div>
                            <CircularProgress style={{ color: 'white' }} />
                            <span style={{ color: 'white', marginTop: '10px', display: 'block' }}>Loading...</span>
                        </div>
                    </div>
                )}

                <input
                    type="text"
                    placeholder="🔍 Search..."
                    value={searchQuery}
                    onChange={handleSearch}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                    style={{
                        width: isFocused ? "240px" : "180px",
                        height: "30px",
                        border: "1px solid #ddd",
                        borderRadius: "20px",
                        padding: "0 14px",
                        fontSize: "12px",
                        transition: "all 0.3s ease",
                        outline: "none",
                    }}
                />

            </div>

            <div className="watermark" >
                <CopyrightIcon /><b style={{ fontFamily: 'serif' }}>EFFTABS SOLUTIONS LLP</b>
            </div>
            <div className='w-full mb-5 flex flex-col gap-4' style={{ maxHeight: '570px', overflowY: 'auto' }}>
                <TableContainer component={Paper}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow style={{ backgroundColor: "#0f172a" }}> {/* Dark navy-blue */}
                                {[
                                    "Sl.No",
                                    "Item",
                                    "Name",
                                    "Description",
                                    "HSN Code",
                                    "UOM",
                                    "GST%",
                                    "Rate",
                                    "",
                                ].map((head) => (
                                    <TableCell
                                        key={head}
                                        style={{
                                            fontSize: "10.5px",
                                            fontWeight: 700,
                                            textTransform: "uppercase",
                                            borderBottom: "2px solid #1e293b",
                                            color: "#f8fafc", // soft white
                                            padding: "10px 12px",
                                            textAlign: ["Rate", "GST%"].includes(head)
                                                ? "right"
                                                : "left",
                                            letterSpacing: "0.5px",
                                            backgroundColor: "#0f172a", // Dark navy-blue
                                            padding: '2px'
                                        }}
                                    >
                                        {head}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {Object.entries(groupedData).map(([workName, items]) => (
                                <>
                                    {/* WorkName group header */}
                                    <TableRow key={workName}>
                                        <TableCell colSpan={9} sx={{
                                            backgroundColor: '#242023ff',
                                            color: 'whitesmoke',
                                            fontWeight: 'bold',
                                            textTransform: 'uppercase',
                                            textAlign: 'center',
                                            fontSize: '13px',
                                            padding: '4px'
                                        }}>
                                            {workName}
                                        </TableCell>
                                    </TableRow>

                                    {/* Items under the work name */}
                                    {items.map((item, idx) => (
                                        <TableRow key={item.Id}>
                                            <TableCell sx={{ padding: '3px', fontSize: '12.5px' }}>{idx + 1}</TableCell>
                                            <TableCell sx={{ padding: '3px', width: '10%', fontSize: '12.5px' }}>{item.WorkItemName}</TableCell>
                                            <TableCell sx={{ padding: '3px', width: '20%', fontSize: '12.5px' }}>{item.Name}</TableCell>
                                            <TableCell sx={{ padding: '3px', width: '40%', fontSize: '12.5px' }}>{item.Description}</TableCell>
                                            <TableCell sx={{ padding: '3px', fontSize: '12.5px', width: '8%' }}>{item.HSNCode}</TableCell>
                                            <TableCell sx={{ padding: '3px', fontSize: '12.5px', width: '5%' }}>{item.UOM}</TableCell>
                                            <TableCell sx={{ padding: '3px', textAlign: 'right', fontSize: '12.5px' }}>{Number(item.GSTRate)}</TableCell>
                                            <TableCell sx={{ padding: '3px', textAlign: 'right', fontSize: '12.5px', width: '8%' }}>{Number(item.Rate)}</TableCell>
                                            <TableCell sx={{ padding: '3px', textAlign: 'right', fontSize: '12.5px', width: '8%' }}>
                                                <Tooltip title="Edit Work Details">
                                                    <Link to={`/WorkDetailsEdit/${item.Id}`}>
                                                        <IconButton color="primary">
                                                            <EditIcon style={{ fontSize: '19px' }} />
                                                        </IconButton>
                                                    </Link>
                                                </Tooltip>
                                                <Tooltip title={item.IsActive ? "Delete Work Details" : "Activate Work Details"}>
                                                    <IconButton
                                                        onClick={() => (
                                                            item.IsActive
                                                                ? openConfirmDialog(item.Id)
                                                                : handleActivate(item.Id)
                                                        )}
                                                        color="error"
                                                    >
                                                        {item.IsActive ? <DeleteIcon style={{ fontSize: '19px' }} /> : <ActivateIcon style={{ fontSize: '19px' }} />}
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                <Dialog
                    open={confirmDialogOpen}
                    onClose={closeConfirmDialog}
                    aria-labelledby="alert-dialog-title"
                    aria-describedby="alert-dialog-description"
                >
                    <DialogTitle id="alert-dialog-title">{"Delete Record?"}</DialogTitle>
                    <DialogContent>
                        <DialogContentText id="alert-dialog-description">
                            Are you sure you want to delete this record?
                        </DialogContentText>
                    </DialogContent>

                    <DialogActions>
                        <button onClick={closeConfirmDialog} className="bg-red-600 text-white border border-red-600 rounded px-4 py-2 mr-2">
                            Cancel
                        </button>
                        <button onClick={handleDelete} className="bg-green-600 text-white border border-green-600 rounded px-4 py-2">
                            Confirm
                        </button>
                    </DialogActions>
                </Dialog>

            </div >
        </div >
    );
}

export default WorkDetailsIndex;

